import { Sparkles, CheckCircle2, Clock } from "lucide-react";

/**
 * Inline brand-accurate (simplified) logo marks.
 * Colors taken from each vendor's public brand guidelines.
 * Kept square, single-mark, no wordmarks — used at small size for a chip UI.
 */

const GoogleSheetsLogo = ({ className = "" }: { className?: string }) => (
  <svg viewBox="0 0 24 24" className={className} aria-hidden>
    <path fill="#0F9D58" d="M19.5 3h-9L4 9.5v10A1.5 1.5 0 0 0 5.5 21h14a1.5 1.5 0 0 0 1.5-1.5v-15A1.5 1.5 0 0 0 19.5 3z"/>
    <path fill="#57BB8A" d="M10.5 3 4 9.5h6.5V3z"/>
    <path fill="#F1F1F1" d="M8 12h9v6H8z"/>
    <path fill="#0F9D58" d="M8 12h9v1H8zm0 2h9v1H8zm0 2h9v1H8zm3-4h1v6h-1zm3 0h1v6h-1z"/>
  </svg>
);

const GmailLogo = ({ className = "" }: { className?: string }) => (
  <svg viewBox="0 0 24 24" className={className} aria-hidden>
    <path fill="#4285F4" d="M3 7.2v11.3c0 .5.4.9.9.9H6V10L3 7.2z"/>
    <path fill="#34A853" d="M18 19.4h2.1c.5 0 .9-.4.9-.9V7.2L18 10v9.4z"/>
    <path fill="#FBBC04" d="M18 7.5v2.5l3-2.8V6.4c0-.9-1-1.4-1.7-.9L18 7.5z"/>
    <path fill="#EA4335" d="M6 10V7.5l6 4.5 6-4.5V10l-6 4.5L6 10z"/>
    <path fill="#C5221F" d="M3 6.4V7.2l3 2.8V7.5L4.7 5.5C4 5 3 5.5 3 6.4z"/>
  </svg>
);

const GoogleFormsLogo = ({ className = "" }: { className?: string }) => (
  <svg viewBox="0 0 24 24" className={className} aria-hidden>
    <path fill="#673AB7" d="M19.5 3h-9L4 9.5v10A1.5 1.5 0 0 0 5.5 21h14a1.5 1.5 0 0 0 1.5-1.5v-15A1.5 1.5 0 0 0 19.5 3z"/>
    <path fill="#B39DDB" d="M10.5 3 4 9.5h6.5V3z"/>
    <circle cx="9" cy="13" r="0.8" fill="#fff"/>
    <rect x="11" y="12.4" width="6" height="1.2" fill="#fff"/>
    <circle cx="9" cy="16" r="0.8" fill="#fff"/>
    <rect x="11" y="15.4" width="6" height="1.2" fill="#fff"/>
  </svg>
);

const SlackLogo = ({ className = "" }: { className?: string }) => (
  <svg viewBox="0 0 24 24" className={className} aria-hidden>
    <path fill="#E01E5A" d="M6.5 14.5a2 2 0 1 1-2-2h2v2zm1 0a2 2 0 0 1 4 0v5a2 2 0 1 1-4 0v-5z"/>
    <path fill="#36C5F0" d="M9.5 6.5a2 2 0 1 1 2-2v2h-2zm0 1a2 2 0 0 1 0 4h-5a2 2 0 1 1 0-4h5z"/>
    <path fill="#2EB67D" d="M17.5 9.5a2 2 0 1 1 2 2h-2v-2zm-1 0a2 2 0 0 1-4 0v-5a2 2 0 1 1 4 0v5z"/>
    <path fill="#ECB22E" d="M14.5 17.5a2 2 0 1 1-2 2v-2h2zm0-1a2 2 0 0 1 0-4h5a2 2 0 1 1 0 4h-5z"/>
  </svg>
);

const NotionLogo = ({ className = "" }: { className?: string }) => (
  <svg viewBox="0 0 24 24" className={className} aria-hidden>
    <rect x="3" y="3" width="18" height="18" rx="2" fill="#fff" stroke="#111" strokeWidth="1.2"/>
    <path fill="#111" d="M8 7.5v9l1.6.4V10l4.8 7 1.6-.3V7.7l-1.5-.4v6.5L9.6 7.1 8 7.5z"/>
  </svg>
);

const AirtableLogo = ({ className = "" }: { className?: string }) => (
  <svg viewBox="0 0 24 24" className={className} aria-hidden>
    <path fill="#FCB400" d="M12 4 3.5 7.3 12 10.6l8.5-3.3L12 4z"/>
    <path fill="#18BFFF" d="M3 8.5v8.2l8 3.3v-8.2L3 8.5z"/>
    <path fill="#F82B60" d="M21 8.5v5l-5 2v-5l5-2z"/>
    <path fill="#BC2026" d="M16 15.5v3.7l5-2v-3.7l-5 2z"/>
  </svg>
);

/* Ambient background marks — simplified silhouettes for visual texture only. */
const HubSpotMark = ({ className = "" }: { className?: string }) => (
  <svg viewBox="0 0 24 24" className={className} aria-hidden>
    <path fill="currentColor" d="M18 9.6V6.5a1.7 1.7 0 1 0-1.4 0v3.1a5 5 0 0 0-2.3 1l-6-4.6a2 2 0 1 0-1 1.3l5.9 4.6a5 5 0 1 0 4.8-2.3zm-.7 7.5a2.6 2.6 0 1 1 0-5.2 2.6 2.6 0 0 1 0 5.2z"/>
  </svg>
);
const TrelloMark = ({ className = "" }: { className?: string }) => (
  <svg viewBox="0 0 24 24" className={className} aria-hidden>
    <rect x="3" y="3" width="18" height="18" rx="3" fill="currentColor"/>
    <rect x="6" y="6" width="5" height="11" rx="1" fill="#fff"/>
    <rect x="13" y="6" width="5" height="7" rx="1" fill="#fff"/>
  </svg>
);
const AsanaMark = ({ className = "" }: { className?: string }) => (
  <svg viewBox="0 0 24 24" className={className} aria-hidden>
    <circle cx="12" cy="7" r="3" fill="currentColor"/>
    <circle cx="7" cy="15" r="3" fill="currentColor"/>
    <circle cx="17" cy="15" r="3" fill="currentColor"/>
  </svg>
);
const CalendlyMark = ({ className = "" }: { className?: string }) => (
  <svg viewBox="0 0 24 24" className={className} aria-hidden>
    <rect x="3" y="5" width="18" height="16" rx="2" fill="currentColor"/>
    <rect x="3" y="5" width="18" height="4" fill="currentColor" opacity="0.7"/>
    <rect x="7" y="3" width="2" height="4" rx="1" fill="currentColor"/>
    <rect x="15" y="3" width="2" height="4" rx="1" fill="currentColor"/>
  </svg>
);
const ZapierMark = ({ className = "" }: { className?: string }) => (
  <svg viewBox="0 0 24 24" className={className} aria-hidden>
    <path fill="currentColor" d="M12 2v6l4-4 2 2-4 4h6v4h-6l4 4-2 2-4-4v6h-4v-6l-4 4-2-2 4-4H2v-4h6L4 6l2-2 4 4V2z"/>
  </svg>
);
const StripeMark = ({ className = "" }: { className?: string }) => (
  <svg viewBox="0 0 24 24" className={className} aria-hidden>
    <rect x="3" y="3" width="18" height="18" rx="3" fill="currentColor"/>
    <path fill="#fff" d="M13 10.5c0-.6.5-.8 1.3-.8 1.1 0 2.5.3 3.7.9V7.4a9.5 9.5 0 0 0-3.7-.7c-3 0-5 1.6-5 4.2 0 4.1 5.7 3.4 5.7 5.2 0 .7-.6.9-1.5.9-1.2 0-2.8-.5-4.2-1.2v3.3c1.4.6 2.9.9 4.2.9 3.1 0 5.2-1.5 5.2-4.2 0-4.4-5.7-3.6-5.7-5.3z"/>
  </svg>
);
const DropboxMark = ({ className = "" }: { className?: string }) => (
  <svg viewBox="0 0 24 24" className={className} aria-hidden>
    <path fill="currentColor" d="M6 4 2 7l4 3 4-3-4-3zm12 0-4 3 4 3 4-3-4-3zM2 13l4 3 4-3-4-3-4 3zm16-3-4 3 4 3 4-3-4-3zM6 17l4 3 4-3-4-3-4 3z"/>
  </svg>
);
const GitHubMark = ({ className = "" }: { className?: string }) => (
  <svg viewBox="0 0 24 24" className={className} aria-hidden>
    <path fill="currentColor" d="M12 2a10 10 0 0 0-3.2 19.5c.5.1.7-.2.7-.5v-2c-2.8.6-3.4-1.2-3.4-1.2-.5-1.2-1.1-1.5-1.1-1.5-.9-.6.1-.6.1-.6 1 .1 1.5 1 1.5 1 .9 1.5 2.4 1.1 3 .8.1-.7.4-1.1.6-1.4-2.2-.3-4.5-1.1-4.5-5 0-1.1.4-2 1-2.7-.1-.3-.5-1.3.1-2.7 0 0 .9-.3 2.8 1a9.5 9.5 0 0 1 5 0c2-1.3 2.8-1 2.8-1 .6 1.4.2 2.4.1 2.7.7.7 1 1.6 1 2.7 0 3.9-2.3 4.7-4.5 5 .4.3.7.9.7 1.8v2.6c0 .3.2.6.7.5A10 10 0 0 0 12 2z"/>
  </svg>
);
const LinearMark = ({ className = "" }: { className?: string }) => (
  <svg viewBox="0 0 24 24" className={className} aria-hidden>
    <path fill="currentColor" d="M3 14.5 9.5 21A10 10 0 0 1 3 14.5zM3 11l10 10a10 10 0 0 0 3-.6L3.6 8a10 10 0 0 0-.6 3zm1.4-5.2 13.8 13.8a10 10 0 0 0 1.6-1.6L6 4.2a10 10 0 0 0-1.6 1.6zM7.7 3.6 20.4 16.3A10 10 0 0 0 7.7 3.6z"/>
  </svg>
);
const IntercomMark = ({ className = "" }: { className?: string }) => (
  <svg viewBox="0 0 24 24" className={className} aria-hidden>
    <rect x="3" y="3" width="18" height="18" rx="4" fill="currentColor"/>
    <path stroke="#fff" strokeWidth="1.4" strokeLinecap="round" d="M8 8v6m4-7v8m4-7v6M8 17c2 1 6 1 8 0"/>
  </svg>
);
const TypeformMark = ({ className = "" }: { className?: string }) => (
  <svg viewBox="0 0 24 24" className={className} aria-hidden>
    <rect x="3" y="3" width="18" height="18" rx="4" fill="currentColor"/>
    <path fill="#fff" d="M8 8h8v2h-3v6h-2v-6H8z"/>
  </svg>
);
const MailchimpMark = ({ className = "" }: { className?: string }) => (
  <svg viewBox="0 0 24 24" className={className} aria-hidden>
    <circle cx="12" cy="12" r="9" fill="currentColor"/>
    <circle cx="9.5" cy="11" r="1.2" fill="#fff"/>
    <circle cx="14.5" cy="11" r="1.2" fill="#fff"/>
    <path stroke="#fff" strokeWidth="1.2" fill="none" strokeLinecap="round" d="M9 15c1 1 4 1 6 0"/>
  </svg>
);

const AMBIENT = [
  // Top band
  { Logo: ZapierMark, top: "2%", left: "38%", size: 18, rotate: 12 },
  { Logo: HubSpotMark, top: "5%", left: "50%", size: 26, rotate: -8 },
  { Logo: TrelloMark, top: "14%", left: "64%", size: 22, rotate: 6 },
  { Logo: IntercomMark, top: "8%", left: "90%", size: 20, rotate: 4 },
  { Logo: TypeformMark, top: "2%", left: "76%", size: 16, rotate: -6 },
  // Upper-mid
  { Logo: DropboxMark, top: "26%", left: "76%", size: 24, rotate: -6 },
  { Logo: GitHubMark, top: "22%", left: "92%", size: 20, rotate: 10 },
  { Logo: MailchimpMark, top: "32%", left: "60%", size: 20, rotate: 14 },
  { Logo: LinearMark, top: "30%", left: "44%", size: 16, rotate: -12 },
  // Mid edges
  { Logo: StripeMark, top: "48%", left: "40%", size: 18, rotate: -10 },
  { Logo: AsanaMark, top: "46%", left: "94%", size: 22, rotate: 8 },
  // Lower-mid
  { Logo: CalendlyMark, top: "60%", left: "62%", size: 20, rotate: 8 },
  { Logo: TrelloMark, top: "64%", left: "92%", size: 18, rotate: -10 },
  { Logo: HubSpotMark, top: "68%", left: "44%", size: 20, rotate: 6 },
  // Bottom band
  { Logo: AsanaMark, top: "78%", left: "52%", size: 24, rotate: -4 },
  { Logo: GitHubMark, top: "82%", left: "70%", size: 20, rotate: 12 },
  { Logo: MailchimpMark, top: "88%", left: "38%", size: 18, rotate: -8 },
  { Logo: DropboxMark, top: "92%", left: "82%", size: 20, rotate: 6 },
  { Logo: ZapierMark, top: "86%", left: "94%", size: 16, rotate: -12 },
  { Logo: TypeformMark, top: "94%", left: "60%", size: 18, rotate: 10 },
];

type Tool = {
  name: string;
  Logo: ({ className }: { className?: string }) => JSX.Element;
  /** Vertical position in % within the left column */
  y: number;
};

const TOOLS: Tool[] = [
  { name: "Forms", Logo: GoogleFormsLogo, y: 6 },
  { name: "Sheets", Logo: GoogleSheetsLogo, y: 24 },
  { name: "Gmail", Logo: GmailLogo, y: 42 },
  { name: "Slack", Logo: SlackLogo, y: 60 },
  { name: "Airtable", Logo: AirtableLogo, y: 78 },
  { name: "Notion", Logo: NotionLogo, y: 96 },
];

export default function WorkflowConvergence() {
  // SVG viewBox is 100x100 — left chips around x=14, hub at x=58, output at x=88
  return (
    <div
      className="relative w-full max-w-[480px] mx-auto rounded-[24px] border border-border bg-white p-5 sm:p-6"
      style={{ boxShadow: "var(--shadow-card)" }}
    >
      <div className="flex items-center justify-between mb-4">
        <span className="badge-pill">Workflow</span>
        <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
          Tools in. Time back.
        </span>
      </div>

      <div className="relative h-[300px]">
        {/* Ambient background logos — scattered, faded for texture */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          {AMBIENT.map((a, i) => (
            <div
              key={`m-${i}`}
              className="absolute text-navy/[0.07]"
              style={{
                top: a.top,
                left: a.left,
                width: a.size,
                height: a.size,
                transform: `rotate(${a.rotate}deg)`,
              }}
            >
              <a.Logo className="w-full h-full" />
            </div>
          ))}
        </div>

        {/* Branching connectors */}
        <svg
          aria-hidden
          viewBox="0 0 100 100"
          preserveAspectRatio="none"
          className="absolute inset-0 w-full h-full pointer-events-none"
        >
          <defs>
            <linearGradient id="conv-line" x1="0" x2="1" y1="0" y2="0">
              <stop offset="0%" stopColor="hsl(var(--border))" stopOpacity="0.4" />
              <stop offset="60%" stopColor="hsl(var(--primary))" stopOpacity="0.55" />
              <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity="0.9" />
            </linearGradient>
            <linearGradient id="conv-out" x1="0" x2="1" y1="0" y2="0">
              <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity="0.9" />
              <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity="0.5" />
            </linearGradient>
          </defs>

          {/* Tributaries from each tool chip into the central hub at (54, 50) */}
          {TOOLS.map((t) => (
            <path
              key={t.name}
              d={`M 22 ${t.y} C 36 ${t.y}, 44 50, 54 50`}
              stroke="url(#conv-line)"
              strokeWidth="0.7"
              fill="none"
              strokeLinecap="round"
            />
          ))}

          {/* Hub ring */}
          <circle cx="54" cy="50" r="4" fill="hsl(var(--primary))" opacity="0.10" />
          <circle cx="54" cy="50" r="2.4" fill="hsl(var(--primary))" opacity="0.22" />
          <circle cx="54" cy="50" r="1.4" fill="hsl(var(--primary))" />

          {/* Short connector into the workflow card */}
          <path
            d="M 58 50 L 60 50"
            stroke="url(#conv-out)"
            strokeWidth="1"
            fill="none"
            strokeLinecap="round"
          />
        </svg>

        {/* Left column: tool chips with real brand marks */}
        <div className="absolute inset-y-0 left-0 w-[44%]">
          {TOOLS.map((t) => (
            <div
              key={t.name}
              className="absolute inline-flex items-center gap-2 rounded-full bg-white border border-border pl-1.5 pr-3 py-1 shadow-sm"
              style={{
                top: `calc(${t.y}% - 14px)`,
                left: 0,
              }}
            >
              <span className="inline-flex items-center justify-center h-6 w-6 rounded-full bg-soft">
                <t.Logo className="h-4 w-4" />
              </span>
              <span className="text-[12px] font-semibold text-navy">{t.name}</span>
            </div>
          ))}
        </div>

        {/* Right column: unified workflow output */}
        <div className="absolute right-0 top-1/2 -translate-y-1/2 w-[44%]">
          <div
            className="rounded-2xl bg-white border border-primary/25 p-4 sm:p-5"
            style={{ boxShadow: "var(--shadow-card)" }}
          >
            <div className="flex items-center gap-2.5">
              <span className="inline-flex items-center justify-center h-8 w-8 rounded-lg bg-lightblue flex-shrink-0">
                <Sparkles className="h-3.5 w-3.5 text-primary" />
              </span>
              <div className="min-w-0">
                <div className="text-[9.5px] font-semibold uppercase tracking-wider text-primary/80">
                  Automated
                </div>
                <div className="text-[13px] font-bold text-navy leading-tight mt-0.5 whitespace-nowrap">
                  One workflow
                </div>
              </div>
            </div>

            <div className="mt-4 pt-3 border-t border-border space-y-2.5">
              <div className="flex items-center gap-2 text-[11.5px] text-navy/85">
                <CheckCircle2 className="h-3 w-3 text-primary flex-shrink-0" />
                <span className="font-medium">Routed</span>
              </div>
              <div className="flex items-center gap-2 text-[11.5px] text-navy/85">
                <CheckCircle2 className="h-3 w-3 text-primary flex-shrink-0" />
                <span className="font-medium">Logged</span>
              </div>
              <div className="flex items-center gap-2 text-[11.5px] text-navy/85">
                <Clock className="h-3 w-3 text-primary flex-shrink-0" />
                <span className="font-medium">Hours saved</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-3 pt-4 border-t border-border flex items-center justify-between text-[11px] text-muted-foreground">
        <span className="font-medium">Tools you already use</span>
        <span className="font-medium">Connected, not replaced</span>
      </div>
    </div>
  );
}
